import { useCallback } from "react";

import { referralLinkUrl } from "@medbillai/utils";

import {
  analyticsService,
  type ReferralShareMethod,
  type ReferralShareOutcome,
} from "../../lib/analytics";
import { useAlerts } from "../../provider/alerts";
import { referralShareMessage } from "./referralShareMessage";

const isShareCancel = (error: unknown) =>
  error instanceof Error && error.name === "AbortError";

const trackShare = (
  method: ReferralShareMethod,
  outcome: ReferralShareOutcome,
) => analyticsService.trackEvent("ReferralShareTapped", { method, outcome });

// Web: the browser's share sheet wherever navigator.share exists, else the
// message goes to the clipboard. navigator.share is called before any await so
// it keeps the tap's user activation, which it then uses up: a failed share
// can't fall back to the clipboard. A real failure rejects so the sheet
// reports it.
export function useShareReferral() {
  const { showAlert } = useAlerts();

  return useCallback(
    async (code: string) => {
      const message = referralShareMessage(
        referralLinkUrl(code, window.location.origin),
      );
      if (typeof navigator.share !== "function") {
        try {
          await navigator.clipboard.writeText(message);
        } catch (error) {
          trackShare("copy", "failed");
          showAlert({
            type: "error",
            message: "Couldn't copy your invite link",
          });
          throw error;
        }
        trackShare("copy", "shared");
        showAlert({ type: "success", message: "Invite link copied" });
        return;
      }
      try {
        await navigator.share({ text: message });
      } catch (error) {
        if (isShareCancel(error)) {
          trackShare("web_share", "cancelled");
          return;
        }
        trackShare("web_share", "failed");
        showAlert({ type: "error", message: "Couldn't open sharing" });
        throw error;
      }
      trackShare("web_share", "shared");
    },
    [showAlert],
  );
}
